/* eslint-disable no-return-assign */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import RidebookForm from './RidebookForm';
import * as ExportState from '../../Export/ExportState';
import { TABLE_TRIP_BOOK } from '../../../Services/TableService';
import PermissionsService, { PERMISSION_REPORT } from '../../../Services/PermissionsService';
import { IDownload } from '../../Export/ExportState';
import { IMessageReportCreated } from '../../SocketConnection/types';
import { postRequest } from '../../../Services/HttpService';
import { AxiosResponse } from 'axios';

interface IVehicleGroupFormProps {
  actionUrl: string;
  formats: string[];
  sorting: any;
  columns: any;
  loader: (value: boolean) => void;
  vehicleId?: any;
  fileName: string;
  tableName: string;
}

export default function TablePrint(props: IVehicleGroupFormProps) {
  const { formatMessage: f } = useIntl();
  const [isOpenMenu, setIsOpenMenu] = React.useState<boolean>(false);
  const [isRidebookFormOpen, setIsRidebookFormOpen] = React.useState<boolean>(false);
  const [isActiveExport, setIsActiveExport] = useRecoilState<false | string>(ExportState.isActive);
  // const [percentage, setPercentage] = useRecoilState<number>(ExportState.percentage);
  const [exportData, setExportData] = useRecoilState<IMessageReportCreated | null>(
    ExportState.data
  );
  // const [exportRequests, setExportRequests] = useRecoilState<string[]>(ExportState.exportRequests);
  const [downloads, setDownloads] = useRecoilState<IDownload[]>(ExportState.downloads);

  const handleEscapeKey = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        if (isOpenMenu) {
          setIsOpenMenu(false);
        }
      }
    },
    [isOpenMenu]
  );

  const handleClickOutside = React.useCallback(
    (event: any) => {
      const { id } = event.target;
      if (isOpenMenu && id !== 'ridebookExportSelect' && id !== 'print' && id !== 'angle-down') {
        setIsOpenMenu(false);
      }
    },
    [isOpenMenu]
  );

  React.useEffect(() => {
    window.addEventListener('click', handleClickOutside);
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('keydown', handleEscapeKey);
    };
  });

  const openMenu = (e: any) => {
    e.preventDefault();
    setIsOpenMenu(!isOpenMenu);
  };

  const handleDownload = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const type = event.currentTarget.getAttribute('data-type');
    props.loader(true);

    const sortJSON: any = {};
    const filterJSON: any = {};
    Object.keys(props.sorting).map((item) => (sortJSON[`${item}`] = props.sorting[item]));
    Object.values(props.columns)
      .filter((item: any) => item.filter !== null && item.filter && item.filter.active === true)
      .map(
        (item: any) =>
          (filterJSON[`${item.name}`] =
            !item.filter.multiple && Array.isArray(item.filter.selectedValue)
              ? item.filter.selectedValue[0]
              : item.filter.selectedValue)
      );
    const data = {
      format: type,
      sort: { ...sortJSON },
      filter: { ...filterJSON },
    };
    if (props.vehicleId) {
      data.filter = {
        ...data.filter,
        vehicleId: props.vehicleId,
      };
    }

    const dateFrom = dayjs(data.filter.timestamp.min).format('DD.MM.YYYY');
    const dateTo = dayjs(data.filter.timestamp.max).format('DD.MM.YYYY');
    let fileTitle = props.fileName.replace('{dateFrom}', dateFrom);
    fileTitle = fileTitle.replace('{dateTo}', dateTo);
    const fileName = `${fileTitle}.${data.format}`;
    if (exportData) {
      setExportData(null);
      // setPercentage(0);
      // setExportRequests([]);
    }

    const res: AxiosResponse | false = await postRequest(props.actionUrl.replace('/api', ''), data, { returnAxiosResponse: true});
    if (res && res.data && res.data.status === 'ok') {
      setIsActiveExport(res.data.jobId);
      setDownloads((oldState: IDownload[]) => {
        const job = oldState.find((item: IDownload) => item.jobId === res.data.jobId);
        if (job) {
          const updatedJob: IDownload = {
            ...job,
            isDownloaded: false,
            code: '',
            percentage: 0,
            generating_end: dayjs().toISOString(),
          };
          const newState = [
            ...oldState.filter((item: IDownload) => item.jobId !== res.data.jobId),
            updatedJob,
          ];
          return newState;
        }
        const newJob: IDownload = {
          jobId: res.data.jobId,
          isDownloaded: false,
          code: '',
          percentage: 0,
          generating_start: dayjs().toISOString(),
          generating_end: null,
          filename: '',
        };
        const newState = [...oldState, newJob];
        return newState;
      });
      // const requests: string[] = JSON.parse(JSON.stringify(exportRequests));
      // if (!requests.includes(res.data.jobId)) {
      //   requests.push(res.data.jobId);
      // }
      // setExportRequests(requests);
    }

    props.loader(false);
  };

  const prepareRidebook = () => {
    setIsRidebookFormOpen(!isRidebookFormOpen);
  };

  const getPermission = () => {
    if (props.tableName !== TABLE_TRIP_BOOK) {
      return true;
    }
    return PermissionsService.hasAccess(`${PERMISSION_REPORT}05`, ['read'], null);
  };

  return getPermission() ? (
    <div
      key="ridebookMenuButtons"
      className={`dropdown d-inline-block ml-xs ${isOpenMenu === true ? 'open' : ''}`}
      style={{ position: 'relative' }}
    >
      <button
        className="e-button dropdown-toggle text-gray"
        id="ridebookExportSelect"
        onClick={openMenu}
        data-cy="table-print"
      >
        <i id="print" className="cmd-icon cmd-icon-print fs-5 position-relative top-1" />
        <i id="angle-down" className="cmd-icon cmd-icon-dropdown ml-2 fs-4" />
      </button>
      <div className="dropdown-menu dropdown-menu-custom fs-2" style={{ cursor: 'pointer' }}>
        {
          props.tableName === TABLE_TRIP_BOOK ? (
            <a
              onClick={prepareRidebook}
              className="px-1 py-1 d-block text-black text-decoration-none"
              data-cy="table-print-ride-book"
            >
              {f({ id: 'tripsOverview.rideBook.prepare' })}{' '}
              <strong>{f({ id: 'tripsOverview.rideBook.ridebookAccusative' })}</strong>
            </a>
          ) : (
            props.formats &&
            props.formats.map((item: string, key: number) => {
              return (
                <a
                  onClick={handleDownload}
                  data-type={item}
                  key={`table-print-${key}`}
                  className={`px-1 py-1 d-block text-black text-decoration-none ${
                    key % 2 === 1 ? 'bg-gray-light' : ''
                  }`}
                  data-cy={`table-print-item-${item}`}
                >
                  {f({ id: 'tripsOverview.rideBook.export' })}
                  <span className={`e-badge ${item} ml-xs`}>{item}</span>
                </a>
              );
            })
          )
          // <a className="px-1 py-1 d-block text-black text-decoration-none">
          //   {f({ id: 'rides.export' }) }
          //   <span className="e-badge pdf ml-xs">PDF</span>
          // </a>
          // <a className="px-1 py-1 d-block bg-gray-light text-black text-decoration-none">
          //   {f({ id: 'rides.export' }) }
          //   <span className="e-badge xls ml-xs">XLS</span>
          // </a>
        }
      </div>
      {isRidebookFormOpen && (
        <div style={{ textAlign: 'left' }}>
          <RidebookForm
            closeCallback={prepareRidebook}
            fileName={props.fileName}
            actionUrl={props.actionUrl}
            formats={props.formats}
          />
        </div>
      )}
    </div>
  ) : null;
}
